import { Selector } from "@reduxjs/toolkit";
import { isEqual, isObject } from "lodash-es";
import { useEffect, useState } from "react";

import { RootState, useAppSelector } from "./index";

export function objectEqualityFn<T>(
  a: T,
  b: T,
  properties?: readonly (keyof NonNullable<T>)[],
): boolean {
  if (a && b) {
    if (!isObject(a) || !isObject(b)) {
      throw new Error("objectEqualityFn can only be used with objects");
    }
    if (properties && properties.length > 0) {
      return properties.every((property) => isEqual(a[property], b[property]));
    }
    const keysA = Object.keys(a) as (keyof NonNullable<T>)[];
    keysA.sort();
    const keysB = Object.keys(b) as (keyof NonNullable<T>)[];
    keysB.sort();
    return keysA.join(",") === keysB.join(",") && keysA.every((key) => a[key] === b[key]);
  }
  return a === b;
}

/**
 * The way Redux works, every time the store changes, it will re-execute all selectors, and see if any of them returns a different value than before. If the selector does something like:
 * sectionId => state => state.sections[id]
 * then there's no issue, as the object for that section will not be different (unless the section changed). However, if you have a selector that returns a list of objects through a filter/map/reduce operation, like so:
 * sectionIds => state => sectionIds.map(sectionId => state.sections[sectionId])
 * the list will get re-built every time the selector gets executed, and the list reference will be different, even though the contents will be the same.
 * This even applies to just calling Object.values(state.sections) in a selector.
 *
 * For such cases, Redux allows you to pass a comparator function as a second argument to useSelector, which will return true/false on whether the selection result actually changed.
 * useListSelector pre-applies a comparator function that checks whether list contents contain the same references, which will fix the detection for most map/filter/reduce results.
 * If we didn't use it, then the components would be re-rendering constantly.
 *
 * See https://www.notion.so/minervaproject/Redux-and-React-patterns-f395d3903b14422ebef19b6f1a91d1a2
 *
 * When `properties` is provided (object element types only), each item is narrowed to Pick of those keys
 * (type-only — runtime still returns full objects). Primitive lists (string[], number[], …) and
 * union array returns (A[] | B[]) use the no-properties overload.
 *
 * `properties` is `readonly K[] & readonly (keyof T)[]` so K must be real keys of T, without
 * `K extends keyof T` (that reverse-infers T from `properties` as {} → never, esp. with inline selectors).
 */
export function useListSelector<T extends readonly unknown[]>(selector: (state: RootState) => T): T;
export function useListSelector<T extends object, const K extends PropertyKey>(
  selector: (state: RootState) => T[],
  properties: readonly K[] & readonly (keyof T)[],
): Pick<T, Extract<K, keyof T>>[];
export function useListSelector(
  selector: (state: RootState) => unknown[],
  properties?: readonly PropertyKey[],
): unknown[] {
  return useAppSelector(selector, (left, right) => {
    if (!Array.isArray(left) || !Array.isArray(right)) {
      throw new Error("useListSelector can only be used with arrays");
    }
    if (left.length !== right.length) {
      return false;
    }

    // For a list of objects, we can limit the comparison to specific properties - use judiciously.
    if (properties && properties.length > 0) {
      return left.every((item, index) => {
        return properties.every((property) =>
          isEqual(
            (item as Record<PropertyKey, unknown>)[property],
            (right[index] as Record<PropertyKey, unknown>)[property],
          ),
        );
      });
    }

    return left.every((item, index) => item === right[index]);
  });
}

/**
 * When `properties` is provided, narrows R to those keys while preserving null/undefined.
 * Runtime still returns the full selector result — the Pick is type-only so callers can't
 * read fields that aren't part of the equality check.
 */
type PickedSelectorResult<R, K extends PropertyKey> =
  | Pick<NonNullable<R>, Extract<K, keyof NonNullable<R>>>
  | Extract<R, null | undefined>;

/**
 * Similar to useListSelector, but for objects.
 * Return type matches the selector's return type (e.g. UserType | null, not UserType | null | undefined).
 * When `properties` is provided, the return type is narrowed to Pick of those keys.
 *
 * `properties` is `readonly K[] & readonly (keyof NonNullable<R>)[]` so K must be real keys of R,
 * without `K extends keyof R` / `NoInfer` (those reverse-collapse R with inline selectors).
 */
export function useObjectSelector<R>(selector: (state: RootState) => R): R;
export function useObjectSelector<R, const K extends PropertyKey>(
  selector: (state: RootState) => R,
  properties: readonly K[] & readonly (keyof NonNullable<R>)[],
): PickedSelectorResult<R, K>;
export function useObjectSelector<R, K extends PropertyKey>(
  selector: (state: RootState) => R,
  properties?: readonly K[],
): R | PickedSelectorResult<R, K> {
  return useAppSelector(selector, (left, right) =>
    objectEqualityFn(left, right, properties as readonly (keyof NonNullable<R>)[] | undefined),
  );
}

export function useConditionalAppSelector<T>(
  selector: Selector<RootState, T | undefined>,
  condition: boolean,
): T | undefined {
  return useAppSelector(condition ? selector : () => undefined);
}

export function useConditionalListSelector<T>(
  selector: Selector<RootState, T[]>,
  condition: boolean,
): T[] {
  return useListSelector(condition ? selector : () => [] as T[]);
}

/**
 * Subscribes to an array selector but only re-renders when `.length` changes.
 * Prefer over useListSelector when you only need the count.
 */
export function useCountSelector(selector: (state: RootState) => readonly unknown[]): number {
  return useAppSelector((state) => selector(state).length);
}

/**
 * Subscribes to an array selector but only re-renders when emptiness changes.
 * Prefer over useListSelector when you only need whether the list is non-empty.
 */
export function useHasItemsSelector(selector: (state: RootState) => readonly unknown[]): boolean {
  return useAppSelector((state) => selector(state).length > 0);
}

type UsePromiseEffectOptions = {
  /** When true, the effect does not run and loading stays at `initialLoading`. */
  skip?: boolean;
  initialLoading?: boolean;
};

/**
 * A hook that wraps a promise-returning function, similar to useEffect, but returns the
 * loading and error states of the promise, re-rendering on changes.
 *
 * Stale responses are ignored when deps change or the component unmounts.
 */
export const usePromiseEffect = <T>(
  effect: () => Promise<T | void> | T | void,
  deps: unknown[],
  options?: UsePromiseEffectOptions,
) => {
  const [isLoading, setIsLoading] = useState(options?.initialLoading ?? true);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<T | null>(null);

  useEffect(() => {
    if (options?.skip) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const result = await effect();
        if (!cancelled) {
          setData(result === undefined ? null : result);
          setError(null);
        }
      } catch (caughtError) {
        if (!cancelled) {
          setError(caughtError instanceof Error ? caughtError : new Error(String(caughtError)));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { isLoading, error, data };
};
